import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { NO_MANUFACTURER_VALUE } from './ProductManufacturerSelect'

/**
 * Stable codes attached to Zod issues (`message`). No locale or i18n paths here —
 * map to `productCatalog` via {@link PRODUCT_FORM_VALIDATION_I18N_KEYS}.
 */
export const PRODUCT_FORM_ERROR_CODES = {
  nameRequired: 'name_required',
  descriptionRequired: 'description_required',
  associatedPartyRequired: 'associated_party_required',
  yearRequired: 'year_required',
  yearMin: 'year_min',
  yearMax: 'year_max',
} as const

export type ProductFormErrorCode =
  (typeof PRODUCT_FORM_ERROR_CODES)[keyof typeof PRODUCT_FORM_ERROR_CODES]

/** Maps Zod issue codes (`message`) to `productCatalog` keys under `dialog.validation`. */
export const PRODUCT_FORM_VALIDATION_I18N_KEYS: Record<
  ProductFormErrorCode,
  string
> = {
  name_required: 'dialog.validation.nameRequired',
  description_required: 'dialog.validation.descriptionRequired',
  associated_party_required: 'dialog.validation.manufacturerRequired',
  year_required: 'dialog.validation.yearRequired',
  year_min: 'dialog.validation.yearMin',
  year_max: 'dialog.validation.yearMax',
}

const codes = PRODUCT_FORM_ERROR_CODES

export const addProductFormSchema = z.object({
  name: z.string().trim().min(1, { message: codes.nameRequired }),
  description: z.string().trim().min(1, { message: codes.descriptionRequired }),
  associatedPartyId: z
    .string()
    .trim()
    .min(1, { message: codes.associatedPartyRequired })
    .refine((value) => value !== NO_MANUFACTURER_VALUE, {
      message: codes.associatedPartyRequired,
    }),
  yearOfManufacture: z
    .string()
    .trim()
    .min(1, { message: codes.yearRequired })
    .refine((yearStr) => /^\d+$/.test(yearStr), {
      message: codes.yearRequired,
    })
    .refine(
      (yearStr) => {
        const yearNum = parseInt(yearStr, 10)
        return yearNum >= 1900
      },
      { message: codes.yearMin }
    )
    .refine(
      (yearStr) => {
        const yearNum = parseInt(yearStr, 10)
        const maxYear = new Date().getFullYear() + 1
        return yearNum <= maxYear
      },
      { message: codes.yearMax }
    ),
  productModel: z.string(),
})

export const addProductFormResolver = zodResolver(addProductFormSchema)

export type AddProductFormValues = z.infer<typeof addProductFormSchema>

export const editProductFormSchema = addProductFormSchema.omit({
  associatedPartyId: true,
})

export const editProductFormResolver = zodResolver(editProductFormSchema)

export type EditProductFormValues = z.infer<typeof editProductFormSchema>

/**
 * Used when validating the “From documents” path before create (toast / UI).
 */
export const ADD_PRODUCT_FROM_DOCUMENTS_ERROR_CODES = {
  filesRequired: 'documents_files_required',
} as const

export type AddProductFromDocumentsErrorCode =
  (typeof ADD_PRODUCT_FROM_DOCUMENTS_ERROR_CODES)[keyof typeof ADD_PRODUCT_FROM_DOCUMENTS_ERROR_CODES]
